/**
 * The site's token → icon-factory vocabulary: one map from a short registry token to the builder
 * that draws it, shared by every surface that names an icon in copy rather than in code.
 *
 * Two consumers today, and they are why this is its own module rather than a private half of
 * `term-enhance.js` (where it lived until the hint-copy icon markers). `term-enhance.js` fills the
 * `[data-term-icon]` spans `TermLinker` server-renders into `{t:...}` glossary links and
 * `{h:...|...|<icon-token>}` help-anchor links; `hints.js` resolves the `{icon:<token>}` markers the
 * hint copy carries into inline marks on the hint float. The map is vocabulary, the walking is not:
 * `term-enhance.js` self-wires on DOMContentLoaded, which is exactly what the game page must never
 * run (`hints.js#showHintReason`'s dynamic-import comment), so the game side imports the vocabulary
 * without the walk.
 *
 * The self-wiring is the whole argument, and the bundle cost is deliberately NOT: `hints.js` is
 * eager, and measured against the prod esbuild build its static import of this module grows the
 * eager boot closure by 1.28 KB gzipped (104.1 → 105.4 KB) — importing `term-enhance.js` whole
 * would have cost ~1.5 KB, since every family it reaches except `position-icons.js` was already in
 * that closure; this import is what makes that one eager too (`generated/eager-modules.js`, 100 →
 * 102). Recorded so a later reader doesn't re-litigate the split on size grounds — re-derive by
 * building each tree with `scripts/deploy/build-frontend.js <outdir> <metafile> <manifest>` and
 * summing gzip over the manifest's `eagerChunks`; the deltas reproduce, the absolutes move with
 * whatever else is on main.
 *
 * `GlossaryTerm#iconToken` is the source for most keys, but `three-legged-stool` / `persona-king` /
 * `lantern` answer to no `GlossaryTerm` at all — they exist purely because a `{h:}` link's copy names them —
 * and the hint markers name only tokens that already exist here. The authoritative key set is
 * therefore the union of all three copy sources; `icon-builders.test.js`'s drift guard scrapes each
 * of them in both directions.
 *
 * Single-element only (pre-PR review finding, both engines independently confirmed), asserted per
 * entry by `icon-builders.test.js` — the shared `icon-slots.js#mountIconSlots` walk spreads an
 * array result, so this map's own test is what holds the invariant, not the walk: the CSS CLS
 * guard (`[data-term-icon]`, skat.css) reserves ONE box for ONE child — a multi-element
 * builder's children each get forced into that same box and stack past it, and `cards/cards.js`'s
 * `createCard` returns a div sized off `--card-height` (undefined here), ignoring the 1em
 * reservation regardless of element count. `GlossaryTerm.java` documents per-term why schmieren,
 * trumpf, and spitzen ship icon-less rather than mount a broken icon.
 *
 * Icons render plain, matching every reference-surface icon (no etch entrance). Every builder below
 * reuses an existing icon-family factory — no new SVG art; a term without a fitting existing icon
 * ships icon-less, and its span simply never gets a map entry. Every builder mounts UNSIZED at the
 * CSS level: `ICON_SIZE` lands as a presentation attribute (`icons/svg.js#svg`), the lowest CSS
 * priority, so each host's own rule (`[data-term-icon] svg`'s fill-the-box rule, `.hint-card-ref
 * svg`'s 0.95em) owns the rendered size. On the hint float the attributes don't even survive that far:
 * `hints.js#frameMarksToInk` strips them after mount, so `width: auto` reads the ink-framed
 * viewBox's aspect rather than this reference size.
 *
 * `pip-clubs`/`pip-spades`/`pip-hearts`/`pip-diamonds` are the registry's own suit-colored icons
 * (the Farben family's sub-entries, `GlossaryTerm#EICHEL` etc. and their reuse in the block-visual
 * tables/chips) — the only entries that pass `createSuitPipSvg` an explicit color rather than
 * relying on `currentColor`, so `.term-link`'s vellum recolor (skat.css § Term links) can't wash
 * them out inside an inline link.
 */
import {
  createChalice,
  createNullZero,
  createFallenChalice,
  createSuitCluster,
} from "./game-type-icons.js";
import {
  createSkatCards,
  createSkatCardsStruck,
  createOuvertFan,
  createScissorsOpen,
  createScissorsClosed,
} from "./prompt-icons.js";
import {
  createBidWeight,
  createAugenTallyArcs,
  createWorthChest,
  createBalanceScale,
} from "./stat-icons.js";
import {
  createGatehouse,
  createGatehouseClaimed,
  createBattleStandardPair,
  createBattleStandard,
} from "./role-icons.js";
import {
  createPositionEar,
  createPositionMouth,
  createPositionHand,
} from "./position-icons.js";
import { createSuitPipSvg } from "../cards/cards.js";
import { suitColor } from "../utils.js";
import { createThreeLeggedStool, createLantern } from "./nav-icons.js";
import { createPersonaKing } from "./persona-icons.js";
import { createMenuIcon } from "./prompt-icons.js";

const ICON_SIZE = 56; // the other reference-surface enhancers' reference size — CSS rescales.

const suitPipBuilder = (suit) => () =>
  createSuitPipSvg(suit, undefined, suitColor(suit));

// Token → builder map (GlossaryTerm#iconToken is the source of truth for most of these — the two
// {h:}-only exceptions are commented at their own entries below). Every builder returns exactly
// one element — the reserved CLS-guard box (skat.css) fits one (see the class comment above);
// a multi-element builder is the bug this map used to carry. Adding an entry here also moves the
// generated per-token widths in skat.css § Term Icon Widths: run
// `node scripts/gates/generate-term-icon-widths.js` (its drift gate fails otherwise).
export const ICON_BUILDERS = {
  weight: () => createBidWeight(ICON_SIZE), // reizen
  "skat-cards-struck": () => createSkatCardsStruck(ICON_SIZE), // hand — the skat stays down
  "tally-arcs": () => createAugenTallyArcs(ICON_SIZE), // augen
  chalice: () => createChalice(ICON_SIZE), // grand
  "null-zero": () => createNullZero(ICON_SIZE), // null
  "fallen-chalice": () => createFallenChalice(ICON_SIZE), // ramsch
  "suit-cluster": () => createSuitCluster(ICON_SIZE), // farbspiel
  "skat-cards": () => createSkatCards(ICON_SIZE), // skat
  worth: () => createWorthChest(ICON_SIZE), // spielwert
  "scissors-open": () => createScissorsOpen(ICON_SIZE), // schneider — the partial cut
  "scissors-closed": () => createScissorsClosed(ICON_SIZE), // schwarz — the full cut
  "ouvert-fan": () => createOuvertFan(ICON_SIZE), // ouvert
  gatehouse: () => createGatehouse(ICON_SIZE), // rollen — the declarer's lone fortress
  "position-ear": () => createPositionEar(ICON_SIZE), // vorhand — hears the first bid
  "position-mouth": () => createPositionMouth(ICON_SIZE), // mittelhand — says it
  "position-hand": () => createPositionHand(ICON_SIZE), // hinterhand — the dealer gives
  "gatehouse-claimed": () => createGatehouseClaimed(ICON_SIZE), // alleinspieler
  "standard-pair": () => createBattleStandardPair(ICON_SIZE), // gegenspieler — the allied pair
  standard: () => createBattleStandard(ICON_SIZE), // mitspieler — the lone ally standard
  "pip-clubs": suitPipBuilder("clubs"), // eichel — also the Grundwert/Farben tables + Trumpf strip
  "pip-spades": suitPipBuilder("spades"), // gruen
  "pip-hearts": suitPipBuilder("hearts"), // herz
  "pip-diamonds": suitPipBuilder("diamonds"), // schellen
  // {h:}-only entries (inline-help-link-icons design § A) — no GlossaryTerm carries either token;
  // both exist purely because a {h:...|...|<icon-token>} link names them.
  "three-legged-stool": () => createThreeLeggedStool(ICON_SIZE), // {h:table|...|three-legged-stool}
  // Mono currentColor, no identityColor — a deliberate deviation from persona-icons.js's identity
  // contract (persona-icons.js docs, § Identity contract), signed off for this inline home: at
  // ~19px identity fill is sub-pixel noise, the King's dark IDENTITY_COLOR.left would drag in the
  // needsRim vellum underlay, and the glossary-link rule (own color/vellum, never amber) is the
  // treatment this inline home wants — same as every other .term-link icon.
  "persona-king": () => createPersonaKing(ICON_SIZE), // {h:personas|Lothar|persona-king}
  // {h:your-games|Your games|lantern} (features-your-games-page) — the third {h:}-only token, the
  // three-legged-stool case again: help.html mounts its own #your-games lantern through
  // help-enhance.js's separate map, so this entry exists purely for the inline link's copy.
  lantern: () => createLantern(ICON_SIZE),
  // {h:menu|pause menu|menu} (features-your-games-page, Andre's acceptance read) — the fourth
  // {h:}-only token: help.html mounts its own #menu mark through help-enhance.js's map.
  menu: () => createMenuIcon(ICON_SIZE),
  // {h:average-mark|…|balance} (features-leaderboard-page) — help.html's own #average-mark entry
  // factory, the same mark the leaderboard detail and the run stats use for an average.
  balance: () => createBalanceScale(ICON_SIZE),
};
